import React from 'react'

import NiceModal from '@ebay/nice-modal-react'
import {
  Box,
  Button,
  Input,
  LoadingOverlay,
  Modal,
  ScrollArea,
  Select,
  Stack,
  Text,
} from '@mantine/core'
import cn from 'classnames'
import { observer } from 'mobx-react-lite'

import { ColumnVisibilityControls } from '#components/RundownToolbar/ColumnVisibilityModal'
import { Keys, useShortcuts } from '#hooks'
import { useMst } from '#state'
import { IRundown } from '#state/types'
import { usePrincePrintableRef } from '#util/princePrinting'
import { RUNDOWN_MARGIN_DEF } from '#util/princePrinting/constants'
import { RundownPrintPrefs } from '#util/zodSchemas/printPreferences'

import { Toast } from '../Toast'

import { orientationData, rowDensityData } from './helpers'
import { PrintableRundownGrid } from './PrintableRundownGrid'

import styles from './PrintPreferences.module.scss'

type PrintModalProps = {
  rundown: IRundown
  printImmediately?: boolean
}

// oxlint-disable-next-line react/only-export-components
const PrintPreferencesModal = observer<
  PrintModalProps & {
    visible: boolean
    hide: () => void
  }
  // oxlint-disable-next-line react/only-export-components
>(function PrintPreferencesModal(props) {
  const { rundown, visible, hide } = props
  const { printRef, handlePrint, loading, showDebugHtml, errorMessage } =
    usePrincePrintableRef(rundown.name)
  const { user, view, nodeEnv } = useMst()
  const handleDebug = showDebugHtml

  const printAndClose = async (download: boolean) => {
    const success = await handlePrint(download)
    if (success) {
      hide()
    }
  }

  useShortcuts({
    print: {
      keys: [Keys.CMD, 'P'],
      action: () => printAndClose(false),
    },
  })

  const handlePrintReady = () => {
    if (props.printImmediately) {
      printAndClose(false)
    }
  }

  const showDebugButton = view.devMode || nodeEnv === 'development'

  // TODO: these should read the settings for margins and page width
  // and compute dynamically
  const pageWidth =
    user.rundownPrintPrefs.orientation === 'landscape' ? 11 : 8.5
  const hMargins = RUNDOWN_MARGIN_DEF.left + RUNDOWN_MARGIN_DEF.right

  const sizingGuideStyle = {
    width: `${pageWidth - hMargins}in`,
  }

  return (
    <Modal
      opened={visible}
      onClose={hide}
      title="Print Rundown"
      size="100%"
      classNames={{
        content: styles.printPreferences_modal,
        body: styles.printPreferences_modalBody,
      }}
    >
      <LoadingOverlay visible={loading} />
      <div className={styles.printPreferences_content}>
        <Stack className={styles.leftColumn}>
          <Input.Wrapper label="Columns to print">
            <ScrollArea.Autosize
              type="auto"
              mah="40vh"
              className={styles.printPreferences_columnVizScroller}
            >
              <Box
                className={styles.printPreferences_columnVizScroller__content}
              >
                <ColumnVisibilityControls rundown={rundown} layout="print" />
              </Box>
            </ScrollArea.Autosize>
          </Input.Wrapper>
          <Select
            label="Orientation"
            value={user.rundownPrintPrefs.orientation}
            onChange={(orientation) => {
              user.updateRundownPrintPrefs({
                orientation: orientation as RundownPrintPrefs['orientation'],
              })
            }}
            data={orientationData}
          />
          <Select
            label="Row Density"
            value={user.rundownPrintPrefs.rowDensity}
            onChange={(rowDensity) => {
              user.updateRundownPrintPrefs({
                rowDensity: rowDensity as RundownPrintPrefs['rowDensity'],
              })
            }}
            data={rowDensityData}
          />

          {errorMessage.length > 0 && (
            <Toast message={errorMessage} type="error" />
          )}

          <Stack gap="xs">
            <Button onClick={() => printAndClose(false)}>Print</Button>
            <Button onClick={() => printAndClose(true)}>Export PDF</Button>
            <Button variant="outline" onClick={hide}>
              Close
            </Button>
          </Stack>

          {showDebugButton && (
            <Button variant="subtle" onClick={handleDebug}>
              Debug Printable HTML
            </Button>
          )}
        </Stack>
        <div className={styles.rightColumn}>
          <div className={styles.guideAndGrid}>
            <div
              className={cn(styles.sizingGuide, styles.hintText)}
              style={sizingGuideStyle}
            >
              <div>Printable area</div>
              <div>Adjust columns to fit width</div>
            </div>
            <PrintableRundownGrid
              ref={printRef}
              rundown={rundown}
              onPrintReady={handlePrintReady}
            />
          </div>
          {rundown.isEditable && (
            <Text
              component="div"
              className={cn(styles.hintText, styles.pageBreakInfo)}
              style={sizingGuideStyle}
            >
              Double-click rows to add page breaks
              <div className={styles.printPreferences_pageBreakDemo} />
            </Text>
          )}
        </div>
      </div>
    </Modal>
  )
})

const NicePrintPreferences = NiceModal.create<PrintModalProps>((props) => {
  const { visible, hide } = NiceModal.useModal()
  return <PrintPreferencesModal visible={visible} hide={hide} {...props} />
})

// See (../../../Modals/readme.md)
export const showPrintPreferences = (rundown: IRundown) =>
  NiceModal.show(NicePrintPreferences, { rundown })

// See (../../../Modals/readme.md)
export const printImmediately = (rundown: IRundown) =>
  NiceModal.show(NicePrintPreferences, { printImmediately: true, rundown })
